import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Globe, Loader2, Search, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

// Carte « Photos — droits d'auteur » (Système → Paramètres, demande du propriétaire
// 2026-10-08) :
//  1) « Mettre à jour les balises meta sur toutes les photos » — intègre dans les
//     métadonnées EXIF de chaque image : © Mr. Trophée, mrtrophee.ca, « Tous droits
//     réservés / All rights reserved » (tâche de fond, progression en direct,
//     courriel de fin à info@trophee.ca).
//  2) « Retrouver mes photos sur le web » — vérification en 1 clic : grille des
//     photos du catalogue, chacune avec un lien qui ouvre Google Lens sur son URL
//     publique pour repérer où elle apparaît en ligne.
export default function ProtectionPhotosCard() {
  const [etat, setEtat] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [totalImages, setTotalImages] = useState(null);
  const [panelOuvert, setPanelOuvert] = useState(false);
  const sondage = useRef(null);

  // Suivi de la progression tant que la tâche tourne
  useEffect(() => {
    if (!etat?.en_cours) {
      if (sondage.current) { clearInterval(sondage.current); sondage.current = null; }
      return;
    }
    sondage.current = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/photos/meta-droit-auteur/statut");
        setEtat(data);
        if (!data.en_cours) {
          toast.success(`Balises meta terminées — ${data.faites} photo(s) modifiée(s) sur ${data.total}`);
        }
      } catch { /* réseau : on réessaie au prochain cycle */ }
    }, 10000);
    return () => { if (sondage.current) { clearInterval(sondage.current); sondage.current = null; } };
  }, [etat?.en_cours]);

  useEffect(() => () => { if (sondage.current) clearInterval(sondage.current); }, []);

  const lancer = async () => {
    try {
      const { data } = await api.post("/admin/photos/meta-droit-auteur");
      if (data.deja_en_cours) {
        toast.info("La mise à jour est déjà en cours — progression affichée ci-dessous.");
      } else {
        toast.success("Mise à jour lancée en arrière-plan — courriel de fin à info@trophee.ca");
      }
      setEtat({ en_cours: true, faites: 0, total: 0, deja: 0, erreurs: 0 });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const ouvrirPanel = async () => {
    if (panelOuvert) { setPanelOuvert(false); return; }
    setPanelOuvert(true);
    if (photos) return;
    try {
      const { data } = await api.get("/admin/photos");
      setPhotos(data.photos || []);
      setTotalImages(data.total_images);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPanelOuvert(false);
    }
  };

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="protection-photos-card">
      <p className="font-semibold flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-gold" /> Photos — droits d'auteur et protection
      </p>
      <p className="text-xs text-ivory/70 mt-1 mb-5">
        Intègre « © Mr. Trophée — mrtrophee.ca — Tous droits réservés » dans les métadonnées de chaque
        photo (la preuve de propriété voyage avec le fichier), puis repérez où vos photos apparaissent en ligne.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={lancer} disabled={!!etat?.en_cours}
          data-testid="meta-photos-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-50">
          {etat?.en_cours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          Mettre à jour les balises meta sur toutes les photos
        </button>
        <button type="button" onClick={ouvrirPanel} data-testid="photos-web-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-gold/50 text-gold hover:bg-gold hover:text-ink transition-colors">
          <Globe className="w-4 h-4" /> Retrouver mes photos sur le web
        </button>
      </div>

      {etat?.en_cours && (
        <p className="text-xs text-emerald-300 mt-4 flex items-center gap-2" data-testid="meta-photos-statut">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          En cours… {etat.faites + etat.deja + etat.erreurs}{etat.total ? ` / ${etat.total}` : ""} photos traitées
          {etat.total ? ` (${etat.faites} modifiées, ${etat.deja} déjà à jour, ${etat.erreurs} erreurs)` : ""}
        </p>
      )}
      {etat && !etat.en_cours && etat.fin && (
        <p className="text-xs text-ivory/50 mt-4" data-testid="meta-photos-derniere">
          Dernière mise à jour terminée : {etat.faites} modifiée(s), {etat.deja} déjà à jour, {etat.erreurs} erreur(s).
        </p>
      )}

      {panelOuvert && (
        <div className="mt-5 border-t border-ivory/10 pt-5" data-testid="photos-web-panel">
          {!photos ? (
            <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement des photos…</p>
          ) : (
            <>
              <p className="text-xs text-ivory/60 mb-3">
                {totalImages} photos sur le site — voici un échantillon (vedettes d'abord). Cliquez « Retrouver »
                pour ouvrir Google Lens sur la photo et voir où elle apparaît en ligne.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {photos.map((p, i) => (
                  <div key={p.url} className="bg-ink border border-ivory/10 rounded-lg p-2" data-testid={`photo-web-${i}`}>
                    <img src={p.url} alt={p.nom} loading="lazy"
                      className="w-full h-20 object-contain rounded mb-1.5 bg-ink" />
                    <p className="text-[10px] text-ivory/70 truncate mb-1.5" title={p.nom}>{p.nom}</p>
                    <a href={p.lens} target="_blank" rel="noopener noreferrer"
                      data-testid={`photo-lens-${i}`}
                      className="inline-flex items-center gap-1 text-[10px] text-gold hover:text-champagne transition-colors">
                      <Search className="w-3 h-3" /> Retrouver sur le web
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
